import {Injectable, OnDestroy} from '@angular/core';
import {
  ActionState,
  ConsumeMode, Currency, DocumentCode, GetPaymentParams,
  OperationType,
  PassFields,
  PassFieldsData,
  PassportType,
  PurposeOfVisit,
  RequestCashType,
  RequestWithDuplicata,
  SearchPaymentQueryParams
} from '../models/request';
import {DialogButton, DialogsService, LangService, LoaderService, NavigateService, ShortcutAction} from 'ngx-satoris';
import {ApiService} from './api.service';
import {DocumentType, UserPermission} from '../models/user';
import {documentTypeIsExit, documentTypeIsEntry, documentTypeIsVisaMultiple, documentTypeIsAnyDouble, getDocumentTypeIsExit, getDocumentTypeIsEntry} from '../models/document-type-helpers';
import {RequestService} from './request.service';
import parse from 'mrz/lib/parse/parse';
import {checkValidity, convertToEpoch, convertToYYMMDD, isDateWithinPeriod} from '../utils/date';
import {CartService, FormApplication} from './cart.service';
import {BehaviorSubject, Subscription} from 'rxjs';
import {environment} from 'src/environments/environment';
import {QueueService} from './queue.service';
import {FieldName, FieldRecords, ParseResult} from 'mrz';
import {Store} from '../models/store';
import {StorageService} from './storage.service';
import {PaymentService} from './payment.service';
import {DialogEditPassportComponent} from 'src/app/components/dialogs/dialog-edit-passport/dialog-edit-passport.component';
import {isEmptyObject} from '../utils/object';
import {hasValue} from '../utils/form';
import {isWithinInterval, subMinutes} from 'date-fns';
import {PassportCode, PlatformEvent} from '../models/information';
import {compressImage} from '../utils/compressImage';

declare const window: any;

@Injectable({
  providedIn: 'root'
})
export class ScanDocumentService implements OnDestroy {

  scanDatas: (ReturnType<typeof parse> & { id?: string; pageWhitePng?: string; pageInfraPng?: string });
  visaDatas: RequestWithDuplicata[];
  visaDatas$: BehaviorSubject<RequestWithDuplicata[]> = new BehaviorSubject([]);
  documentError: string;
  documentWarning: string;
  documentPhoto: string;
  consumeMode: ConsumeMode | null = null;
  consumeMode$: BehaviorSubject<ConsumeMode> = new BehaviorSubject(undefined);
  scanned = false;
  isScanning = false;

  scannedPassportType: PassportType;
  relevantIn: RequestWithDuplicata[] = [];
  relevantOut: RequestWithDuplicata[] = [];
  relevantOtherStates: RequestWithDuplicata[] = [];

  nationality: string;
  isNoDeclaration: boolean;
  documentNumber: string;
  isVisaExist: RequestWithDuplicata;
  zweRefugee: boolean;
  otherRecordsButton: DialogButton[];

  private signOutSubscription: Subscription;

  constructor(private loader: LoaderService,
              private lang: LangService,
              public api: ApiService,
              private nav: NavigateService,
              private cart: CartService,
              private request: RequestService,
              private storage: StorageService,
              private payment: PaymentService,
              private queue: QueueService,
              private dialog: DialogsService) {
    if(this.isRegistered()) {
      const scanDatas = sessionStorage.getItem('scanDatas');
      const visaDatas = sessionStorage.getItem('visaDatas');
      this.scanDatas = scanDatas ? JSON.parse(scanDatas) : null;
      this.documentError = sessionStorage.getItem('documentError') || '';
      this.documentWarning = sessionStorage.getItem('documentWarning') || '';
      this.documentPhoto = sessionStorage.getItem('documentPhoto') || '';
      this.visaDatas = visaDatas ? JSON.parse(visaDatas) : [];
    }

    this.signOutSubscription = this.api.signOutObservable.subscribe({
      next: () => {
        this.resetScanDatas();
      }
    });
    this.consumeMode = localStorage.getItem('consumeMode') as ConsumeMode;
  }

  ngOnDestroy() {
    this.signOutSubscription.unsubscribe();
  }

  scanPassport() {
    this.resetScanDatas();
    if(this.isScanning) return;
    if(!this.consumeMode) {
      return this.loader.loading(true, {type: 'warn', message: this.lang.transform('scan.noConsumeMode')});
    } else {
      this.isScanning = true;
      this.loader.loading(true);
      if(this.api.isElectronApp && this.api.env.enableWelcomeFlow) electron.step('scanning', false, []);
      return this.scanDocument().then(() => {
        if(this.api.isElectronApp && this.scanDatas?.fields && this.api.env.enableWelcomeFlow) {
          const f = this.scanDatas.fields;
          electron.step('scanned', false, [f.firstName || '', f.lastName || '', f.nationality || '', f.documentNumber || '', this.documentPhoto || '']);
        }
        this.api.status = 'info.searchingPayment';
        const queryParams: SearchPaymentQueryParams = {searchIndexed: this.scanDatas.fields.documentNumber};
        return this.api.searchPayment(queryParams, undefined, 'DESC', undefined, 20).then(resCall => {
          this.loader.loading(false);
          if(resCall?.result) {
            this.nationality = this.scanDatas.fields.nationality;
            this.isNoDeclaration = this.api.env.nationalityNoDeclaration.includes(this.scanDatas.fields.nationality);
            this.documentNumber = this.scanDatas.fields.documentNumber;
            this.zweRefugee = this.isZweRefugee(this.scanDatas.fields.nationality, this.scanDatas.fields.issuingState);
            const code = this.scanDatas.fields.documentCode;
            const scannedPassportIsZweRefugee = this.isZweRefugee(this.scanDatas.fields.nationality, this.scanDatas.fields.issuingState);
            this.scannedPassportType = this.getPassportTypeFromCode(code, scannedPassportIsZweRefugee);
            const reqs: RequestWithDuplicata[] = resCall.result;
            reqs.map((req: RequestWithDuplicata) => this.payment.processOfflineData(req));
            this.saveMultiple(reqs, {history: true, watchlist: false, apipnr: false, inbound: false, fullMetadata: false});
            const nat = this.scanDatas.fields.nationality;
            const requestFilter = (nat === 'CHN' || nat === 'HKG') ? reqs.filter(p => p.indexed.Nationality === 'CHN' || p.indexed.Nationality === 'HKG') : reqs.filter(p => p.indexed.Nationality === nat);
            if(requestFilter.length) {
              const relevantRequests = requestFilter.filter((request: RequestWithDuplicata) => {
                // We filter the requests to keep only the ones that have the same passport number, same nationality, not in the sync queue and with a valid usage date
                const isSameDocumentNumberAndNationality = request.metadata.PassportNumber === this.scanDatas.fields.documentNumber && ((this.scanDatas.fields.nationality === 'CHN' || this.scanDatas.fields.nationality === 'HKG') ? (request.metadata.Nationality === 'CHN' || request.metadata.Nationality === 'HKG') : request.metadata.Nationality === this.scanDatas.fields.nationality);
                const isSyncing = this.cart.syncQueue.find(sync => sync.paymentId === request.id) || this.queue.requestInQueue(request.id);
                const isUsageAfterValid = this.request.isUsageDateValid(request.usageAfter, 'usageAfter');
                const isUsageUntilValid = !request.usageUntil || this.request.isUsageDateValid(request.usageUntil, 'usageUntil');
                return isSameDocumentNumberAndNationality && isUsageAfterValid && isUsageUntilValid && !isSyncing;
              });
              this.isVisaExist = relevantRequests.find(request => request.metadata.PassportNumber === this.documentNumber && ((this.nationality === 'CHN' || this.nationality === 'HKG') ? (request.metadata.Nationality === 'CHN' || request.metadata.Nationality === 'HKG') : request.metadata.Nationality === this.nationality));
              this.relevantIn = this.request.filterRequestsByActionState(relevantRequests, [ActionState.READY_CHECKIN, ActionState.READY_CHECK, ActionState.REFUSED]);
              this.relevantOut = this.request.filterRequestsByActionState(relevantRequests, [ActionState.READY_CHECKOUT, ActionState.READY_ZWEEXIT, ActionState.REFUSED]);
              this.relevantOtherStates = this.relevantIn.concat(this.relevantOut).filter(request => {
                const relevantDocument = this.api.userInfo.server.paymentRequests.find(d => d.id === request.operationId);
                return (!documentTypeIsExit(request.operationId) && !documentTypeIsEntry(request.operationId)) &&
                  (request.checkoutIsNext ? (request.usageCount < relevantDocument.usageCount) : true) &&
                  (request.passportType !== this.scannedPassportType || request.metadata.PassportType !== this.scannedPassportType);
              });
              this.otherRecordsButton = this.relevantOtherStates.length > 0 ? [{
                label: this.lang.transform('review.otherRecords'),
                size: 'sm',
                action: () => {
                  // we add this flag to know that the request was found by other states
                  this.relevantOtherStates.forEach(request => request.viewOutOfState = true);
                  this.visaDatas = this.relevantOtherStates;
                  this.visaDatas$.next(this.visaDatas);
                  this.registerScanDatasToSession();
                  this.nav.to('admin-scanned-list', undefined, {queryParams: {fromRoute: 'dashboard'}});
                }
              }] as DialogButton[] : undefined;
            }
            return this.scanPassportAction(requestFilter, 'dashboard');
          }
        });
      }).catch((error: any) => {
        this.loader.loading(false);
        this.isScanning = false;
        this.api.status = undefined;
        if(error.includes('Timeout')) {
          const createButton = [{
            label: this.lang.transform('scanTimeout.create'),
            size: 'sm',
            action: () => this.scanPassportAction([], 'dashboard', undefined, true)
          }] as DialogButton[];
          return this.loader.loading(true, {type: 'warn', message: 'scan.timeout', additionalButtons: createButton, btnLabel: 'global.retry'}).then((retry: any) => {
            if(retry) {
              this.scanPassport();
              return;
            }
          });
        } else {
          return this.loader.loading(true, {type: 'warn', message: error});
        }
      }).finally(() => {
        this.isScanning = false;
        this.api.status = undefined;
      });
    }
  }

  scanMrzAndFilterRequests(resetScanDataAfterProcessing = true) {
    this.resetScanDatas();
    if(this.isScanning) return;

    const navigateWithMrzParams = () => {
      this.loader.loading(false);
      const queryParams: Record<string, string> = {};
      if(this.scanDatas?.fields?.documentNumber) queryParams.mrzPassportNumber = this.scanDatas.fields.documentNumber;
      if(this.scanDatas?.fields?.firstName) queryParams.mrzFirstName = this.scanDatas.fields.firstName;
      if(this.scanDatas?.fields?.lastName) queryParams.mrzLastName = this.scanDatas.fields.lastName;
      if(this.scanDatas?.fields?.nationality) queryParams.mrzNationality = this.scanDatas.fields.nationality;
      this.nav.to('admin-request', undefined, {queryParams});
    };

    const handleError = (error: any) => {
      this.loader.loading(false);
      const msg = typeof error === 'string' ? error : (error?.message || 'scan.error');
      return this.loader.loading(true, {type: 'warn', message: msg});
    };

    const handleFinally = () => {
      this.isScanning = false;
      this.api.status = undefined;
      if(resetScanDataAfterProcessing) this.resetScanDatas();
    };

    if(this.api.isElectronApp) {
      // In Electron, use the same native reader path as scanPassport. This avoids WebSerial requestPort() which still requires a user
      this.isScanning = true;
      this.loader.loading(true);
      return this.scanDocument()
        .then(navigateWithMrzParams)
        .catch(handleError)
        .finally(handleFinally);
    }

    if(!electronLocal?.getMrzSerial) {
      return this.loader.loading(true, {type: 'warn', message: this.lang.transform('err.noReader')});
    }
    this.isScanning = true;
    // getMrzSerial must be called synchronously within the user gesture context before any await
    const mrzPromise = electronLocal.getMrzSerial();
    this.loader.loading(true);
    return mrzPromise
      .then(([mrzResult]: [MrzReadResult, string[]]) => {
        this.scanDatas = mrzResult;
        if(this.scanDatas?.fields?.documentNumber) {
          this.scanDatas.fields.documentNumber = this.scanDatas.fields.documentNumber.replace(/\s/g, '');
        }
        navigateWithMrzParams();
      })
      .catch(handleError)
      .finally(handleFinally);
  }

  private async testDocument(chip = false, data?: any, mrz?: string) {
    // Check if there's an active fake passport
    if(this.api.devtoolFakePassport?.data) {
      return [null, this.api.devtoolFakePassport.face || null, null, this.api.devtoolFakePassport.data, null];
    }

    let response;
    for await (response of (chip ? electronLocal.testDocumentChip(data, mrz, true, false) : electronLocal.testDocument(false, true, false))) {
      if(response.length < 2) {
        this.api.status = undefined;
        throw response[0];
      }
      this.api.status = response[0];
    }
    this.api.status = undefined;
    return response;
  }

  private scanDocument() {
    let timeoutScan : NodeJS.Timeout;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutScan = setTimeout(() => reject('Timeout'), 10000);
    });
    return Promise.race([
      this.testDocument(),
      timeoutPromise
    ]).then(result => {
      let [err, photo, , infos, certBytes] = result as [string, Uint8Array, Uint8Array, MrzReadResult, Uint8Array];
      const action = () => {
        this.scanDatas = infos;

        if(this.scanDatas?.fields?.documentNumber) {
          this.scanDatas.fields.documentNumber = this.scanDatas.fields.documentNumber.replace(/\s/g, '');
        }

        // Check if the document is a refugee document
        if(this.isRefugee(this.scanDatas.fields.nationality, this.scanDatas.fields.issuingState, this.scanDatas.fields.documentCode)) {
          this.scanDatas.fields.documentCode = PassportCode.PR;
          this.scanDatas.fields.nationality = 'XXC';
        }

        this.api.status = 'info.convertingImage';
        if(this.api.isElectronApp) {
          const photoPromise = typeof photo === 'string'
            ? Promise.resolve(photo)
            : electron.convertJp2Jpg(photo).catch(() => undefined);
          return photoPromise.then(photoUrl => {
            this.api.status = undefined;
            switch(err) {
            case 'err.noPassiveAuthenticationCSCA':
              this.documentWarning = err;
              break;
            default:
              this.documentError = err;
              break;
            }

            const isSoonIssued = this.scanDatas?.fields?.issueDate && isDateWithinPeriod(this.scanDatas.fields.issueDate, '1 month');
            if(isSoonIssued) {
              this.documentWarning = 'err.passport.issuedSoon';
            }
            const isValid = checkValidity(this.scanDatas.fields.expirationDate);
            if(!isValid) {
              if(this.scanDatas?.fields?.nationality === this.api.userInfo.server.country && this.consumeMode === ConsumeMode.ENTRY) this.documentWarning = 'err.passport.expired';
              else this.documentError = 'err.passport.expired';
            }
            this.documentPhoto = photoUrl;
            if(this.documentPhoto && !this.documentError) {
              this.documentError = 'passport.valid';
            }
            if(!environment.production) {
              if((this.documentWarning && this.documentWarning === 'err.noPassiveAuthenticationCSCA') || (this.documentError && this.documentError !== 'passport.valid' && this.documentError !== 'err.noCard')) {
                const encodedCertBytes = certBytes ? btoa(String.fromCharCode.apply(null, certBytes)) : undefined;
                window.sendTicket(`[ERROR Scan passport] - Agent ID : ${this.api.userInfo.id}`, this.lang.transform('glpi.' + (this.documentWarning === 'err.noPassiveAuthenticationCSCA' ? this.documentWarning : this.documentError)), `${this.api.userInfo.name}`, `${this.api.userInfo.accountName}`, this.lang.transform('error.passportScan', {certBytes: encodedCertBytes}));
              }
            }
          });
        }
      };
      const generateCheckDigit = (string: string) => {
        let code = 0;
        const factors = [7, 3, 1];
        for(let i = 0; i < string.length; i++) {
          let charCode = string.charCodeAt(i);
          if(charCode === 60) charCode = 0;
          if(charCode >= 65) charCode -= 55;
          if(charCode >= 48) charCode -= 48;
          charCode *= factors[i % 3];
          code += charCode;
        }
        return code %= 10;
      };
      const actionInvalid = (fieldsToChange: {[key: string]: boolean}) => {
        const data = {
          [PassFields.FIRST_NAME]: infos?.fields?.firstName || '',
          [PassFields.LAST_NAME]: infos?.fields?.lastName || '',
          [PassFields.BIRTH_DAY]: infos?.fields?.birthDate ? convertToEpoch(infos.fields.birthDate, false, true) : '',
          [PassFields.DOCUMENT_NUMBER]: infos?.fields?.documentNumber || '',
          [PassFields.SEX]: infos?.fields?.sex || '',
          [PassFields.NATIONALITY]: infos?.fields?.nationality || '',
          [PassFields.EXPIRATION_DATE]: infos?.fields?.expirationDate  ? convertToEpoch(infos.fields.expirationDate) : '',
          [PassFields.DOCUMENT_CODE]: infos?.fields?.documentCode || '',
          submitted: true,
          fieldsToChange: ((err === 'err.noCard' && !infos.valid) || err === 'err.noBAC') ? undefined : fieldsToChange
        };
        return this.dialog.openDialog(DialogEditPassportComponent, 'md', data, false, undefined, true).then((updatedData: PassFieldsData) => updatedData);
      };

      const isInfoInvalids = this.checkScannedDatas(infos);
      if(!isEmptyObject(isInfoInvalids) || err === 'err.noBAC') {
        return this.loader.loading(true, {type: 'warn', message: this.lang.transform('DYN.scanPassport.badFields'), btnLabel: 'understand', hideCloseBtn: true, custom: {backdrop: false, disableClose: true}, shortcut: {btn: {action: ' ' as ShortcutAction}}}).then(() => actionInvalid(isInfoInvalids).then((updatedDatas) => {
          if(updatedDatas) {
            if(updatedDatas.firstName) {
              infos.fields.firstName = updatedDatas.firstName.toUpperCase();
            }
            if(updatedDatas.lastName) {
              infos.fields.lastName = updatedDatas.lastName.toUpperCase();
            }
            if(updatedDatas.birthDate) {
              const birthDate = convertToYYMMDD(new Date(updatedDatas.birthDate));
              infos.fields.birthDate = birthDate;
            }
            if(updatedDatas.documentNumber) {
              infos.fields.documentNumber = updatedDatas.documentNumber;
            }
            if(updatedDatas.sex) {
              infos.fields.sex = updatedDatas.sex;
            }
            if(updatedDatas.nationality) {
              infos.fields.nationality = updatedDatas.nationality;
            }
            if(updatedDatas.expirationDate) {
              const expirationDate = convertToYYMMDD(new Date(updatedDatas.expirationDate));
              infos.fields.expirationDate = expirationDate;
            }
            let fullLengthDocNumber = infos.fields.documentNumber;
            while(fullLengthDocNumber.length < 9) fullLengthDocNumber = fullLengthDocNumber + '<';
            return err === 'err.noBAC' ? /* Retry once reading the chip */ this.testDocument(true, infos, fullLengthDocNumber + generateCheckDigit(fullLengthDocNumber)
                + infos.fields.birthDate + generateCheckDigit(infos.fields.birthDate) + infos.fields.expirationDate + generateCheckDigit(infos.fields.expirationDate)).then(result => {
              [err, photo, , infos, certBytes] = result as [string, Uint8Array, Uint8Array, MrzReadResult, Uint8Array];
              action();
            }) : action();
          }
        }));
      } else {
        return action();
      }
    }).catch(err => {
      const message = err === 'err.noReader' ? err : 'err.scanPassport';
      return this.loader.loading(true, {type: 'warn', message: message, btnLabel: message === 'err.scanPassport' ? 'global.reload' : ''}).then((reload: boolean) => {
        if(reload) {
          return window.location.reload();
        }
        return Promise.reject(err);
      });
    }).finally(() => {
      clearTimeout(timeoutScan);
    });
  }

  resetScanDatas() {
    this.scanDatas = undefined;
    this.documentError = undefined;
    this.documentWarning = undefined;
    this.documentPhoto = undefined;
    this.visaDatas = undefined;
    this.visaDatas$.next(this.visaDatas);

    this.scannedPassportType = undefined;
    this.relevantIn = [];
    this.relevantOut = [];
    this.relevantOtherStates = [];

    this.nationality = undefined;
    this.isNoDeclaration = false;
    this.documentNumber = undefined;
    this.isVisaExist = undefined;
    this.zweRefugee = false;
    this.otherRecordsButton = undefined;
    sessionStorage.removeItem('scanDatas');
    sessionStorage.removeItem('documentError');
    sessionStorage.removeItem('documentWarning');
    sessionStorage.removeItem('documentPhoto');
    sessionStorage.removeItem('visaDatas');
  }

  changeConsume(mode: ConsumeMode, clean = true) {
    if(localStorage.getItem('consumeMode') === mode && clean) {
      localStorage.removeItem('consumeMode');
      this.consumeMode = null;
      this.consumeMode$.next(null);
    } else {
      this.consumeMode = mode;
      this.consumeMode$.next(mode);
      localStorage.setItem('consumeMode', mode);
    }
    if(this.consumeMode === ConsumeMode.TRANSIT) {
      this.loader.loading(true, {type: 'info', custom: {icon: 'arrows-turn-to-dots', innerHtml: this.lang.transform('consumeMode.transit')}});
    }
  }

  /**
   * Action to perform after scanning a passport and getting the datas from the searchPayment API
   * @param datas Datas from the searchPayment API
   * @param from  'dashboard' or 'admin-request' (where the scan was made)
   * @param params Additional params to pass to the next route
   * @returns
   */
  scanPassportAction(datas: RequestWithDuplicata[], from: 'dashboard' | 'admin-request', params?: any, offline = false) {
    window.sessionStorage.removeItem('previousIdApplication');
    const scanPassportDone = () => {
      if(!this.isVisaExist) {
        // If no visa was found in the database
        if(this.consumeMode === ConsumeMode.ENTRY || this.consumeMode === ConsumeMode.TRANSIT) {
          if(this.isNoDeclaration || this.zweRefugee) {
            // In case of the user is a refugee or from a country that doesn't need a declaration, we directly create an entry visa
            return this.register(getDocumentTypeIsEntry(), from, params);
          } else {
            // Start IF1 to create a new entry visa
            return this.loader.loading(true, {type: 'info', message: 'scan.noEntryFound', btnLabel: 'yes', custom: {icon: 'file-circle-question', closeBtnLabel: 'no'}}).then((done) => {
              if(done) {
                this.scanned = true;
                this.cart.startApplication(true);
              } else {
                this.registerScanDatasToSession(false);
              }
            });
          }
        } else {
          if(this.api.hasPerm(UserPermission.ALLOW_CONSUME) && !this.api.hasPerm(UserPermission.ALLOW_CREATE)) {
            return this.loader.loading(true, {type: 'warn', message: 'allowConsume.noCreate'});
          }
          // Same as above but for exit visa
          if(this.isNoDeclaration) {
            return this.register(getDocumentTypeIsExit(), from, params);
          } else {
            return this.loader.loading(true, {type: 'info', message: this.lang.transform('scan.noVisaFound'), btnLabel: 'global.confirm'}).then((done) => {
              if(done) {
                return this.register(getDocumentTypeIsExit(), from, params);
              } else {
                this.registerScanDatasToSession(false);
              }
            });
          }
        }
      } else {
        if(this.consumeMode === ConsumeMode.ENTRY || this.consumeMode === ConsumeMode.TRANSIT) {
          if(this.relevantIn.length === 1) {
            // If we found only one visa in the database, we directly go to the admin select page
            this.visaDatas = [];
            this.visaDatas$.next(this.visaDatas);
            sessionStorage.removeItem('visaDatas');
            this.scanDatas.id = this.relevantIn[0].id;
            this.registerScanDatasToSession();
            return this.nav.to('admin-request-select/' + this.relevantIn[0].id, undefined,  {queryParams: {from: from, foundByScan: true, ...params}});
          } else if(this.relevantIn.length > 1) {
            const processSingleRequest = (request: RequestWithDuplicata) => {
              this.visaDatas = [];
              this.visaDatas$.next(this.visaDatas);
              sessionStorage.removeItem('visaDatas');
              this.scanDatas.id = request.id;
              this.registerScanDatasToSession();
              return this.nav.to('admin-request-select/' + request.id, undefined, {queryParams: {from, foundByScan: true, ...params}});
            };
            const isReadyCheckin = this.relevantIn.filter(request => this.request.getStatusTheme(request).actionState === ActionState.READY_CHECKIN);
            const isMultiple = isReadyCheckin.filter(request => documentTypeIsVisaMultiple(request.operationId));
            const isDouble = isReadyCheckin.filter(request => documentTypeIsAnyDouble(request.operationId));
            // We prioritize the multiple visa type, then the double visa type and finally the single visa type
            if(isMultiple.length === 1) {
              return processSingleRequest(isMultiple[0]);
            }
            if(isDouble.length === 1) {
              return processSingleRequest(isDouble[0]);
            }
            if(isReadyCheckin.length === 1) {
              return processSingleRequest(isReadyCheckin[0]);
            }
            this.visaDatas = this.relevantIn;
            this.visaDatas$.next(this.visaDatas);
            this.registerScanDatasToSession();
            return this.nav.to('admin-scanned-list', undefined, {queryParams: {fromRoute: from, ...params}});
          } else {
            // We have found one or more visa in the database but none of them are in the correct state
            this.noVisaFoundEntryTransitAction(from, params);
          }
        } else {
          // Same as above but for exit visa
          if(this.relevantOut.length === 1) {
            this.visaDatas = [];
            this.visaDatas$.next(this.visaDatas);
            sessionStorage.setItem('visaDatas', JSON.stringify(this.visaDatas));
            this.scanDatas.id = this.relevantOut[0].id;
            this.registerScanDatasToSession();
            return this.nav.to('admin-request-select/' + this.relevantOut[0].id, undefined,  {queryParams: {from, foundByScan: true, ...params}});
          } else if(this.relevantOut.length > 1) {
            const isMultiple = this.relevantOut.filter(request => documentTypeIsVisaMultiple(request.operationId));
            if(isMultiple.length === 1) {
              this.visaDatas = [];
              this.visaDatas$.next(this.visaDatas);
              sessionStorage.setItem('visaDatas', JSON.stringify(this.visaDatas));
              this.scanDatas.id = isMultiple[0].id;
              this.registerScanDatasToSession();
              return this.nav.to('admin-request-select/' + isMultiple[0].id, undefined,  {queryParams: {from, foundByScan: true, ...params}});
            }
            this.visaDatas = this.relevantOut;
            this.visaDatas$.next(this.visaDatas);
            this.registerScanDatasToSession();
            return this.nav.to('admin-scanned-list', undefined, {queryParams: {fromRoute: from, ...params}});
          } else {
            // We have found one or more visa in the database but none of them are in the correct state
            this.noVisaFoundExitAction(from, params);
          }
        }
      }
    };
    const allEventsFromScan = datas.map(request => request.events).flat();
    const operationType = this.consumeMode === ConsumeMode.ENTRY || this.consumeMode === ConsumeMode.TRANSIT ? OperationType.IN : OperationType.OUT;

    if(!this.consumeMode || !allEventsFromScan || !operationType) {
      return scanPassportDone();
    }
    return this.checkHasBeenUsedRecently(allEventsFromScan, operationType, offline).then(() => scanPassportDone());
  }

  registerPayment(scanDetails: ReturnType<typeof parse>, type: DocumentType, zweRefugee = false) {
    if(this.api.isOnline) {
      const registerParams =  [
        undefined,
        type,
        '',
        RequestCashType.CASH,
        this.api.userPlaceId,
        undefined,
        undefined,
        undefined,
        undefined,
        JSON.stringify({
          FirstName: scanDetails.fields.firstName,
          PassportType: documentTypeIsEntry(type) ? this.scannedPassportType : undefined,
          LastName: scanDetails.fields.lastName,
          BirthDay: convertToEpoch(scanDetails.fields.birthDate, false, true),
          Gender: scanDetails.fields.sex,
          Nationality: scanDetails.fields.nationality,
          PassportNumber: scanDetails.fields.documentNumber,
          BorderPass: documentTypeIsExit(type) ? 'No' : undefined,
          FundsAvailable: 0,
          FundsAvailableCurrency: 'USD',
          Documents: this.consumeMode === ConsumeMode.TRANSIT ? {PurposeOfVisit: PurposeOfVisit.TRANSIT_VISA} : (this.consumeMode === ConsumeMode.ENTRY && zweRefugee) ? {PurposeOfVisit: PurposeOfVisit.LIVE_HERE} : undefined,
          PassportCountry: (this.consumeMode === ConsumeMode.ENTRY && zweRefugee) ? 'ZWE' : undefined
        }),
        this.cart.getUniqueReference([]),
        undefined,
        environment.appVersion.join('.'),
        new Date().getTime()
      ];
      return this.queue.addToQueue('registerCashPayment', registerParams, 5000, undefined, undefined, true);
    }
  }

  registerScanDatasToSession(visa = true) {
    if(this.scanDatas) {
      sessionStorage.setItem('scanDatas', JSON.stringify(this.scanDatas));
    }
    if(this.documentError) {
      sessionStorage.setItem('documentError', this.documentError);
    }
    if(this.documentWarning) {
      sessionStorage.setItem('documentWarning', this.documentWarning);
    }
    if(this.documentPhoto) {
      sessionStorage.setItem('documentPhoto', this.documentPhoto);
    }
    if(!visa) {
      sessionStorage.removeItem('visaDatas');
    } else {
      if(this.visaDatas) {
        sessionStorage.setItem('visaDatas', JSON.stringify(this.visaDatas));
      }
    }
    if(this.documentPhoto && !this.documentError) {
      this.documentError = 'passport.valid';
      sessionStorage.setItem('documentError', this.documentError);
    }
  }

  isRegistered() {
    const scanDatas = sessionStorage.getItem('scanDatas');
    const visaDatas = sessionStorage.getItem('visaDatas');
    if(visaDatas) {
      const parsedVisaDatas = JSON.parse(visaDatas);
      return parsedVisaDatas ? true : false;
    }
    if(scanDatas) {
      const parsedScanDatas = JSON.parse(scanDatas);
      return parsedScanDatas && parsedScanDatas.id ? true : false;
    }
    return false;
  }

  isZweRefugee(nationality: string, issuingState: string) {
    const refugeeNat = ['XXA', 'XXB', 'XXC'];
    return (refugeeNat.includes(nationality) && issuingState === 'ZWE');
  }

  isRefugee(nationality?: string, issuingState?: string, docType?: string): boolean {
    if(!nationality && !issuingState && !docType) return false;
    if(docType === PassportCode.PL) return false;
    if(issuingState === 'XBA') return false;
    const refugeeNat = ['XXA', 'XXB', 'XXC', 'XXX'];

    return (
      (docType && docType[0] === 'P' && nationality && issuingState && nationality !== issuingState) ||
      (nationality && refugeeNat.includes(nationality)) ||
      (docType && ['PR', 'PT'].includes(docType))
    );
  }

  /**
   * Allow to override the scan document details
   * @param overrides The fields to override
   */
  updateScanDocumentDetails(overrides: Partial<FieldRecords>) {
    Object.keys(overrides).forEach(key => {
      const fieldKey = key as keyof FieldRecords;
      if(this?.scanDatas.fields[fieldKey] !== undefined) {
        this.scanDatas.fields[fieldKey] = overrides[fieldKey]!;
      }
    });
  }

  /**
   * Update the payment with the passport data and update the scan document details (optional)
   * @param paymentId Payment id
   * @param overrides The fields to override
   * @param updateScanDocumentDetails Update the scan document details
   * @returns
   */
  transferFromPassportData(paymentId: string, overrides: Partial<FieldRecords> = this?.scanDatas.fields, updateScanDocumentDetails = false) {
    const {
      [PassFields.BIRTH_DAY]: BirthDay,
      [PassFields.FIRST_NAME]: FirstName,
      [PassFields.LAST_NAME]: LastName,
      [PassFields.NATIONALITY]: Nationality,
      [PassFields.DOCUMENT_NUMBER]: PassportNumber,
      [PassFields.SEX]: Gender,
      [PassFields.EXPIRATION_DATE]: DateOfExpiry
    } = overrides;
    const datas: {
      FirstName?: string;
      LastName?: string;
      Nationality?: string;
      PassportNumber?: string;
      Gender?: string;
      BirthDay?: number;
      DateOfExpiry?: number;
    } = {};
    if(FirstName) datas.FirstName = FirstName;
    if(LastName) datas.LastName = LastName;
    if(Nationality) datas.Nationality = Nationality;
    if(PassportNumber) datas.PassportNumber = PassportNumber;
    if(Gender) datas.Gender = Gender;
    if(hasValue(BirthDay, 'date')) datas.BirthDay = typeof BirthDay === 'number' ? BirthDay : parseInt(BirthDay!, 10);
    if(hasValue(DateOfExpiry, 'date')) datas.DateOfExpiry = typeof DateOfExpiry === 'number' ? DateOfExpiry : parseInt(DateOfExpiry!, 10);
    return this.api.updatePayment(paymentId, undefined, undefined, JSON.stringify(datas), undefined, undefined, undefined).then(() => {
      if(updateScanDocumentDetails) {
        this.updateScanDocumentDetails(overrides);
      }
    });
  }

  saveMultiple(requests: RequestWithDuplicata[], callParams: GetPaymentParams) {
    requests.reduce((prevPromise, req) => prevPromise.finally(() => {
      this.storage.getFromStorage(Store.PAYMENT_STORE, req.id).then((savedFound: RequestWithDuplicata) => {
        if(!savedFound?.savedCallParams || this.payment.hasPropertyChangedToTrue(savedFound.savedCallParams, callParams)) {
          req.savedCallParams = callParams;
          this.storage.saveToStorage(Store.PAYMENT_STORE, req).catch(error => {
            console.error(`Error saving ${req.id}:`, error);
          });
        }
      });
    }), Promise.resolve());
  }

  checkScannedDatas(scannedData: ParseResult): {[key: string]: boolean} {
    const nameRegex = /^[A-Za-z\s\-]+$/;
    const documentNumberRegex = /^[A-Za-z0-9\s\-]+$/;
    const isFieldPresent = (field: string | undefined): boolean => !!field && field.trim() !== '';
    const getFieldValue = (fieldName: FieldName): string | undefined => {
      const scannedValue = scannedData.fields[fieldName];
      return scannedValue || undefined;
    };
    const firstName = getFieldValue('firstName');
    const lastName = getFieldValue('lastName');
    const nationality = getFieldValue('nationality');
    const documentNumber = getFieldValue('documentNumber')?.replace(/\s/g, '');
    const birthDate = getFieldValue('birthDate');
    const expirationDate = getFieldValue('expirationDate');
    const sex = getFieldValue('sex');
    const errors: {[key: string]: boolean} = {};
    if(!isFieldPresent(firstName) || !nameRegex.test(firstName)) errors.firstName = true;
    if(lastName && !nameRegex.test(lastName)) errors.lastName = true;
    if(!isFieldPresent(nationality)) errors.nationality = true;
    if(!isFieldPresent(sex) || !sex || !['male', 'female', 'm', 'f'].includes(sex.toLowerCase())) errors.sex = true; else scannedData.fields.sex = sex.toLowerCase() === 'm' ? 'male' : sex.toLowerCase() === 'f' ? 'female' : sex.toLowerCase();
    if(!isFieldPresent(documentNumber) || !scannedData.details.find(d => d.field === 'documentNumber')?.valid || !documentNumberRegex.test(documentNumber!)) errors.documentNumber = true;
    if(!isFieldPresent(birthDate) || !scannedData.details.find(d => d.field === 'birthDate')?.valid) errors.birthDate = true;
    if(!isFieldPresent(expirationDate) || !scannedData.details.find(d => d.field === 'expirationDate')?.valid) errors.expirationDate = true;
    return errors;
  }

  async getPagePngDataUrl(white: boolean): Promise<string> {
    const dataInput = white ? this.scanDatas?.pageWhitePng : this.scanDatas?.pageInfraPng;
    if(!dataInput) return undefined;
    try {
      const base64Data = dataInput;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for(let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {type: 'image/png'});
      const file = new File([blob], 'document.png', {type: 'image/png'});
      const compressedFile = await compressImage(file);
      const reader = new FileReader();
      return new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(compressedFile);
      });
    } catch (e) {
      return undefined; // `data:image/png;base64,${dataInput}`;
    }
  }

  /**
   * Maps passport code to passport type. Starting 1/1/2026, new passport codes are used.
   * This method provides a mapping from both old and new codes to their respective passport types.
   */
  private getPassportTypeFromCode(code: string, isZweRefugee: boolean): PassportType {
    // Check if passport was issued after 1/1/2026
    const issueDateStr = this.scanDatas?.fields?.issueDate;
    const isAfterNewCodeDate = issueDateStr ? new Date(issueDateStr) >= new Date('2026-01-01') : false;
    if(isZweRefugee) {
      return PassportType.REFUGEE;
    }
    // Handle old codes (pre-2026) and legacy codes
    if(!isAfterNewCodeDate) {
      // Old mapping for codes used before 2026
      if(code === PassportCode.PR || code === PassportCode.PT) {
        return PassportType.REFUGEE;
      } else if(code === PassportCode.PD) {
        return PassportType.DIPLOMATIC;
      } else if(code === PassportCode.PO) {
        return PassportType.SERVICE;
      } else if(code === PassportCode.PL) {
        return PassportType.LAISSEZ_PASSER;
      } else if(code === PassportCode.PM) {
        return PassportType.MARINE;
      } else {
        return PassportType.ORDINARY;
      }
    }
    // Handle new codes (from 2026 onwards) - map to closest existing passport type
    switch(code) {
    case PassportCode.PP:  // Ordinary passport
      return PassportType.ORDINARY;
    case PassportCode.PO:  // Official / Service
      return PassportType.SERVICE;
    case PassportCode.PD:  // Diplomatic
      return PassportType.DIPLOMATIC;
    case PassportCode.PE:  // Emergency
      return PassportType.ORDINARY;
    case PassportCode.PR:  // Refugee passport
      return PassportType.REFUGEE;
    case PassportCode.PT:  // Alien passport
      return PassportType.REFUGEE;
    case PassportCode.PS:  // Stateless
      return PassportType.REFUGEE;
    case PassportCode.PL:  // Laissez-passer
      return PassportType.LAISSEZ_PASSER;
    case PassportCode.PM:  // Military
      return PassportType.MARINE;
    case PassportCode.PU:  // Emergency single-sheet
      return PassportType.ORDINARY;
    default:
      return PassportType.ORDINARY;
    }
  }

  changeToHKG() {
    this.scanDatas.fields.nationality = 'HKG';
  }

  checkHasBeenUsedRecently(events: PlatformEvent[], operationType: OperationType, offline = false): Promise<void> {
    const operationFound: PlatformEvent = events?.find(event => event.indexed.Operation === operationType && isWithinInterval(new Date(event.updatedOn), {start: subMinutes(new Date(), 15), end: new Date()}));
    let offlineFound = false;
    if(offline) {
      const documentNumber = this.scanDatas.fields.documentNumber;
      const nationality = this.scanDatas.fields.nationality;
      if(this.queue.isRequestRecentInQueue(documentNumber, nationality, this.consumeMode, 15)) {
        offlineFound = true;
      }
    }
    if(operationFound || offlineFound) {
      let message = '';
      if(this.consumeMode === ConsumeMode.ENTRY || this.consumeMode === ConsumeMode.TRANSIT) {
        message = 'scan.alreadyIn';
      } else {
        message = 'scan.alreadyOut';
      }

      return this.loader.loading(true, {type: 'error', message, btnLabel: 'sure', custom: {
        icon: 'clone',
        backdrop: false,
        disableClose: true
      }, shortcut: {
        show: false
      }}).then((done: boolean) => {
        if(done) {
          return Promise.resolve();
        } else {
          return Promise.reject();
        }
      });
    } else {
      return Promise.resolve();
    }
  }

  private register (type: DocumentType, from?: 'dashboard' | 'admin-request', params?: any) {
    if(documentTypeIsExit(type)) {
      const prefillDatas = {
        firstName: this.scanDatas.fields.firstName,
        lastName: this.scanDatas.fields.lastName,
        birthDay: convertToEpoch(this.scanDatas.fields.birthDate, false, true),
        gender: this.scanDatas.fields.sex,
        nationality: this.scanDatas.fields.nationality,
        passportNr: this.scanDatas.fields.documentNumber
      };
      return this.nav.to('admin-request-create', undefined, {queryParams: {fromRoute: from, prefill: JSON.stringify(prefillDatas),  ...params}});
    } else {
      this.loader.loading(true);
      return this.registerPayment(this.scanDatas, type, this.zweRefugee).then((res) => {
        this.loader.loading(false);
        if(res.queueId) {
          const offlineData: FormApplication = {travelName: '', plannedEntry: null, currentPerson: null, requests: [], visaFeesAndTerms: false};
          offlineData.queueId = res.queueId;
          offlineData.offlineParentMethodName = 'registerCashPayment';
          offlineData.requests.push({
            visaType: {
              id: type,
              amountCents: 0,
              currency: Currency.USD
            },
            metadata: {
              BirthDay: convertToEpoch(this.scanDatas.fields.birthDate, true, true),
              FirstName: this.scanDatas.fields.firstName,
              LastName: this.scanDatas.fields.lastName,
              Gender: this.scanDatas.fields.sex,
              Nationality: this.nationality,
              PassportNumber: this.documentNumber,
              ExpiryDate: convertToEpoch(this.scanDatas.fields.expirationDate, true),
              IssueDate: new Date(this.scanDatas.fields.issueDate),
              Documents: this.consumeMode === ConsumeMode.TRANSIT ? {PurposeOfVisit: PurposeOfVisit.TRANSIT_VISA} : (this.consumeMode === ConsumeMode.ENTRY && this.zweRefugee) ? {PurposeOfVisit: PurposeOfVisit.LIVE_HERE} : undefined
            }
          } as RequestWithDuplicata);
          this.registerScanDatasToSession();
          return this.nav.to('admin-request-select/' + null, undefined, {queryParams: {from, foundByScan: true, ...params, offlineApplication: JSON.stringify(offlineData)}});
        } else {
          const paymentId = res.id ? res.id : res.ids[0];
          this.scanDatas.id = paymentId;
          this.registerScanDatasToSession();
          return this.nav.to('admin-request-select/' + paymentId, undefined, {queryParams: {from, foundByScan: true, ...params}});
        }
      }).catch(() => this.loader.loading(true, {type: 'warn', message: this.lang.transform('scan.error')}));
    }
  }

  private noVisaFoundExitAction(from?: 'dashboard' | 'admin-request', params?: any) {
    if(this.api.hasPerm(UserPermission.ALLOW_CONSUME) && !this.api.hasPerm(UserPermission.ALLOW_CREATE)) {
      return this.loader.loading(true, {type: 'warn', message: 'allowConsume.noCreate'});
    }
    if(this.isNoDeclaration) {
      return this.register(getDocumentTypeIsExit(), from, params);
    } else {
      return this.loader.loading(true, {type: 'info', message: `scan.noVisaFound${this.relevantOtherStates.length ? '.otherRecords' : ''}`, additionalButtons: this.otherRecordsButton, btnLabel: this.relevantOtherStates.length ? 'createNewOne' : 'yes', custom: {icon: 'file-circle-question'}}).then((done) => {
        if(done) {
          return this.register(getDocumentTypeIsExit(), from, params);
        } else {
          this.resetScanDatas();
        }
      });
    }
  }

  private noVisaFoundEntryTransitAction(from?: 'dashboard' | 'admin-request', params?: any) {
    if(this.isNoDeclaration || this.zweRefugee) {
      return this.register(getDocumentTypeIsEntry(), from, params);
    } else {
      // Ask if the worker want to create a new entry visa, or view other records
      return this.loader.loading(true, {type: 'info', message: `scan.noEntryFound${this.relevantOtherStates.length ? '.otherRecords' : ''}`, btnLabel: this.relevantOtherStates.length ? 'createNewOne' : 'yes', additionalButtons: this.otherRecordsButton, custom: {icon: 'file-circle-question', closeBtnLabel: this.relevantOtherStates.length ? 'global.close' :'no'}}).then((done) => {
        if(done) {
          this.scanned = true;
          this.cart.startApplication(true);
        } else if(this.otherRecordsButton?.length) {
          this.otherRecordsButton[0].action();
        } else {
          this.resetScanDatas();
        }
      });
    }
  }

  documentCodeToName(documentCodeOverride? : DocumentCode): string {
    const documentCode = documentCodeOverride ? documentCodeOverride : this.scanDatas?.fields?.documentCode;
    if(!documentCode || !Object.values(DocumentCode).includes(documentCode as DocumentCode)) {
      return this.lang.transform('DYN.document.' + DocumentCode.OTHER);
    }
    return this.lang.transform('DYN.document.' + documentCode);
  }
}
